import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import { Popover, type PopoverAlign, type PopoverSide } from "@/components/ds";

// A row-actions / insert menu, in place of @datasirpi/ui's <Menu> (cascade
// plan 3.1: the one vendor family ds/ has no copy of). Built on ds/Popover,
// so it portals, flips, closes on Escape and outside click, and returns focus
// to its trigger like every other ds/ overlay. Both spellings the pages use
// work: <Menu.Trigger>/<Menu.Items>/<Menu.Item> and the bare
// MenuTrigger/MenuItems/MenuItem exports.
//
// Eval F29 makes it an APG menu button: the trigger says aria-haspopup="menu"
// and points aria-controls at the role="menu" itself (not the popover panel
// around it); ArrowDown/ArrowUp on the trigger open it; the menu is one Tab
// stop (roving tabindex) with Arrow/Home/End between items; Escape, choosing
// an item and Tab all close it and put focus back on the trigger — Tab then
// carries on from there, so it never strands focus at the end of <body>.

/** Every item role the roving tabindex moves between. Plain elements with
 *  these roles take part too (the account menu's theme radios). */
const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]';

interface MenuContextValue {
  close: () => void;
  menuId: string;
  triggerId: string;
}

const MenuContext = createContext<MenuContextValue | null>(null);

export interface MenuProps {
  align?: PopoverAlign;
  side?: PopoverSide;
  children: ReactNode;
}

export interface MenuTriggerProps {
  /** A single focusable element (a button or IconButton). */
  children: ReactElement;
}

export interface MenuItemsProps {
  className?: string;
  /** Names the menu. Defaults to the trigger's name (aria-labelledby). */
  "aria-label"?: string;
  children: ReactNode;
}

export interface MenuItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** Runs on click or Enter, then the menu closes. Skipped when aria-disabled or the click was prevented. */
  onSelect?: () => void;
  /** Draws the item in the danger text colour (remove, delete). */
  destructive?: boolean;
  /** Trailing content: a keyboard hint or an info tooltip. */
  shortcut?: ReactNode;
}

export function MenuTrigger({ children }: MenuTriggerProps) {
  return children;
}

function menuItems(menu: HTMLElement): HTMLElement[] {
  return Array.from(menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
    (el) => !(el as HTMLButtonElement).disabled
  );
}

export function MenuItems({ className, "aria-label": ariaLabel, children }: MenuItemsProps) {
  const ctx = useContext(MenuContext);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // The item holding the one tab stop. Kept in the DOM rather than in state
  // so items that are not <MenuItem> (radios, checkboxes) can take part.
  const activeRef = useRef<HTMLElement | null>(null);

  const applyRoving = () => {
    const menu = menuRef.current;
    if (!menu) return;
    const items = menuItems(menu);
    if (!activeRef.current || !items.includes(activeRef.current)) activeRef.current = items[0] ?? null;
    for (const el of menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)) {
      el.tabIndex = el === activeRef.current ? 0 : -1;
    }
  };

  // After every render: items may have been added, removed or disabled.
  useLayoutEffect(applyRoving);

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.matches(ITEM_SELECTOR)) return;
    activeRef.current = target;
    applyRoving();
  };

  // Arrow keys move between items; Home/End jump. Tab closes the menu and
  // returns to the trigger without preventing the default, so the browser
  // then moves on from the trigger as if the menu had never been open.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Tab") {
      ctx?.close();
      return;
    }
    const items = menuItems(e.currentTarget);
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === "ArrowDown") next = at < 0 ? 0 : (at + 1) % items.length;
    else if (e.key === "ArrowUp") next = at <= 0 ? items.length - 1 : at - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    if (next < 0) return;
    e.preventDefault();
    items[next].focus();
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      id={ctx?.menuId}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : ctx?.triggerId}
      className={cn("rc-menu", className)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    >
      {children}
    </div>
  );
}

export function MenuItem({ onSelect, destructive, shortcut, className, onClick, children, ...rest }: MenuItemProps) {
  const ctx = useContext(MenuContext);
  const inert = rest["aria-disabled"] === true || rest["aria-disabled"] === "true";
  return (
    <button
      type="button"
      role="menuitem"
      // MenuItems hands the one tab stop round; -1 until it does.
      tabIndex={-1}
      className={cn("rc-menu__item", destructive && "rc-menu__item--destructive", className)}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || inert || rest.disabled) return;
        onSelect?.();
        ctx?.close();
      }}
      {...rest}
    >
      <span className="rc-menu__label">{children}</span>
      {shortcut ? <span className="rc-menu__shortcut">{shortcut}</span> : null}
    </button>
  );
}

interface TriggerSlotProps {
  trigger: ReactElement;
  menuId: string;
  triggerId: string;
  // Injected by ds/Popover's cloneElement.
  "aria-expanded"?: boolean;
  onClick?: (e: MouseEvent) => void;
}

// ds/Popover clones its child with aria-controls pointing at its own panel.
// This slot takes those props and re-applies them to the real trigger with
// the menu's own id and role, so the trigger is announced as a menu button.
function TriggerSlot({ trigger, menuId, triggerId, "aria-expanded": expanded, onClick: toggle }: TriggerSlotProps) {
  const own = trigger.props as {
    onClick?: (e: MouseEvent) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
  };
  return cloneElement(trigger, {
    id: triggerId,
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(expanded),
    "aria-controls": expanded ? menuId : undefined,
    onClick: (e: MouseEvent) => {
      own.onClick?.(e);
      toggle?.(e);
    },
    onKeyDown: (e: KeyboardEvent) => {
      own.onKeyDown?.(e);
      if (e.defaultPrevented || expanded) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        toggle?.(e as unknown as MouseEvent);
      }
    },
  } as Record<string, unknown>);
}

function MenuRoot({ align = "start", side = "bottom", children }: MenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const fallbackTriggerId = useId();
  let trigger: ReactElement | null = null;
  let items: ReactNode = null;
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === MenuTrigger) trigger = (child.props as MenuTriggerProps).children;
    else if (child.type === MenuItems) items = child;
  });
  if (!trigger) throw new Error("Menu needs a <Menu.Trigger> child.");
  const triggerEl = trigger as ReactElement;
  const triggerId = ((triggerEl.props as { id?: string }).id) ?? fallbackTriggerId;
  const close = () => {
    setOpen(false);
    // Popover returns focus only on Escape; a chosen item unmounts with the
    // panel, so without this focus would fall to <body>.
    document.getElementById(triggerId)?.focus();
  };
  return (
    <MenuContext.Provider value={{ close, menuId, triggerId }}>
      <Popover open={open} onOpenChange={setOpen} align={align} side={side} content={items} className="rc-menu-panel">
        <TriggerSlot trigger={triggerEl} menuId={menuId} triggerId={triggerId} />
      </Popover>
    </MenuContext.Provider>
  );
}

export const Menu = Object.assign(MenuRoot, { Trigger: MenuTrigger, Items: MenuItems, Item: MenuItem });
